import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Popover,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import SyncAltOutlinedIcon from '@mui/icons-material/SyncAltOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import { useAuth } from '../context/AuthContext';
import {
  fetchMyNotifications,
  fetchUnreadNotificationCount,
  markNotificationRead,
  markAllNotificationsRead,
  InAppNotification,
} from '../graphqlService';
import { brand, neutral, statusTone } from '../theme';
import { formatDateTime, formatRelativeTime } from '../utils/format';

const POLL_INTERVAL_MS = 30000;
const PANEL_ID = 'notifications-panel';
const TITLE_ID = 'notifications-panel-title';

// The bell sits on the dark (ink) app bar, like the Help icon beside it.
const ON_DARK = brand.ivory;
const ON_DARK_MUTED = alpha(brand.ivory, 0.72);
const ON_DARK_HOVER_BG = alpha(brand.ivory, 0.08);

const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

/** One neutral, outlined icon per kind of notification. */
const typeIcon = (type: string) => {
  const sx = { fontSize: 20 };
  switch (type) {
    case 'PROJECT_ASSIGNED':
      return <AssignmentIndOutlinedIcon sx={sx} />;
    case 'REVISION_SUBMITTED':
      return <RateReviewOutlinedIcon sx={sx} />;
    case 'REVISION_REQUESTED':
      return <EditNoteOutlinedIcon sx={sx} />;
    case 'REVISION_ACCEPTED':
      return <TaskAltOutlinedIcon sx={sx} />;
    case 'PROJECT_NOTE':
      return <ChatBubbleOutlineOutlinedIcon sx={sx} />;
    case 'STATUS_CHANGE':
      return <SyncAltOutlinedIcon sx={sx} />;
    case 'EDITOR_INVITATION':
      return <PersonAddAltOutlinedIcon sx={sx} />;
    default:
      return <NotificationsNoneOutlinedIcon sx={sx} />;
  }
};

const NotificationRowContent: React.FC<{ notification: InAppNotification }> = ({ notification: n }) => {
  const when = formatRelativeTime(n.createdAt);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%' }}>
      <Box sx={{ display: 'flex', color: 'text.secondary', mt: '2px' }} aria-hidden="true">
        {typeIcon(n.type)}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {!n.read && (
          <Box component="span" sx={visuallyHidden}>
            Unread:{' '}
          </Box>
        )}
        <Typography component="span" variant="body2" sx={{ display: 'block', fontWeight: n.read ? 500 : 700, color: 'text.primary' }}>
          {n.title}
        </Typography>
        {n.message && (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
            {n.message}
          </Typography>
        )}
        {when && (
          <Typography
            component="time"
            dateTime={n.createdAt}
            title={formatDateTime(n.createdAt)}
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.25 }}
          >
            {when}
          </Typography>
        )}
      </Box>
      {!n.read && (
        <Box
          aria-hidden="true"
          sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: statusTone.active.main, flexShrink: 0, mt: 0.75 }}
        />
      )}
    </Box>
  );
};

const NotificationBell: React.FC = () => {
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState<InAppNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const open = Boolean(anchorEl);

  const refreshCount = useCallback(async () => {
    if (!user) return;
    try {
      const count = await fetchUnreadNotificationCount();
      setUnreadCount(count);
    } catch (error) {
      // Runs on a timer — an unhandled rejection here surfaces as a console
      // error every poll and can tear down the interval.
      console.warn('Could not refresh unread notification count:', error);
    }
  }, [user]);

  // Poll the unread count while signed in.
  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      setItems([]);
      return;
    }
    refreshCount();
    pollRef.current = setInterval(refreshCount, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [user, refreshCount]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const list = await fetchMyNotifications(30, false);
      setItems(list);
    } catch (error) {
      console.error('Could not load notifications:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    loadItems();
  };

  const handleClose = () => setAnchorEl(null);

  /** Marks the notification read; a linked one navigates through its link. */
  const handleItemClick = (n: InAppNotification) => {
    handleClose();
    if (!n.read) {
      setItems((prev) => prev.map((i) => (i.id === n.id ? { ...i, read: true } : i)));
      setUnreadCount((count) => Math.max(0, count - 1));
      // Optimistic; if marking fails, the next count check corrects the badge.
      markNotificationRead(n.id)
        .then(setUnreadCount)
        .catch(() => undefined);
    }
  };

  const handleMarkAll = async () => {
    try {
      const newCount = await markAllNotificationsRead();
      setUnreadCount(newCount);
      setItems((prev) => prev.map((i) => ({ ...i, read: true })));
    } catch {
      // Nothing changes on screen, so nothing is claimed; the next check refreshes it.
    }
  };

  if (!user) return null;

  const buttonLabel = unreadCount > 0 ? `Notifications, ${unreadCount > 99 ? '99+' : unreadCount} unread` : 'Notifications';
  const hasUnreadItems = items.some((i) => !i.read);

  const rowSx = (read: boolean) => ({
    alignItems: 'flex-start',
    px: 2,
    py: 1.5,
    backgroundColor: read ? 'transparent' : neutral[100],
    '&:hover': { backgroundColor: neutral[200] },
    '&.Mui-focusVisible': { backgroundColor: neutral[200], outline: `2px solid ${brand.copper}`, outlineOffset: -2 },
  });

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          onClick={handleOpen}
          aria-label={buttonLabel}
          aria-haspopup="dialog"
          aria-expanded={open ? 'true' : undefined}
          aria-controls={open ? PANEL_ID : undefined}
          sx={{
            color: open ? ON_DARK : ON_DARK_MUTED,
            '&:hover': { color: ON_DARK, backgroundColor: ON_DARK_HOVER_BG },
          }}
        >
          <Badge badgeContent={unreadCount} color="error" max={99} overlap="circular">
            <NotificationsNoneOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        id={PANEL_ID}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-labelledby': TITLE_ID,
            sx: { width: 380, maxWidth: 'calc(100vw - 32px)', mt: 1, borderRadius: 3, overflow: 'hidden' },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography id={TITLE_ID} component="h2" sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>
            Notifications
          </Typography>
          {hasUnreadItems && (
            <Button size="small" onClick={handleMarkAll} sx={{ mr: -1 }}>
              Mark all as read
            </Button>
          )}
        </Box>
        <Divider />

        {loading ? (
          <Box role="status" aria-label="Loading notifications" sx={{ px: 2, py: 1.5 }}>
            {[0, 1, 2].map((i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1.5, py: 1 }}>
                <Skeleton variant="circular" width={20} height={20} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="55%" />
                  <Skeleton width="85%" />
                </Box>
              </Box>
            ))}
          </Box>
        ) : loadError ? (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              We couldn&apos;t load your notifications.
            </Typography>
            <Button size="small" variant="outlined" onClick={loadItems}>
              Retry
            </Button>
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ px: 3, py: 5, textAlign: 'center' }}>
            <NotificationsNoneOutlinedIcon sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} aria-hidden="true" />
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              You&apos;re all caught up
            </Typography>
            <Typography variant="body2" color="text.secondary">
              New activity on your projects shows up here.
            </Typography>
          </Box>
        ) : (
          <List aria-label="Notifications" disablePadding sx={{ maxHeight: 420, overflowY: 'auto' }}>
            {items.map((n, index) => (
              <ListItem key={n.id} disablePadding divider={index < items.length - 1}>
                {n.link ? (
                  <ListItemButton component={RouterLink} to={n.link} onClick={() => handleItemClick(n)} sx={rowSx(n.read)}>
                    <NotificationRowContent notification={n} />
                  </ListItemButton>
                ) : (
                  <ListItemButton onClick={() => handleItemClick(n)} sx={rowSx(n.read)}>
                    <NotificationRowContent notification={n} />
                  </ListItemButton>
                )}
              </ListItem>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
};

export default NotificationBell;
